import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Stack, Chip, IconButton, Divider, Snackbar, Alert, Tooltip, LinearProgress,
    CircularProgress, Skeleton,
} from '@mui/material';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { syncRolesFromApi } from '../redux/slices/rolesSlice';
import { selectUserTypeId } from '../redux/slices/authSlice';
import { canAssignRole } from '../data/roleAccess';
import { ATTENDANCE_MODES, DEFAULT_ATTENDANCE_MODE, attendanceModeOf } from '../data/attendanceModes';
import http, { apiErrorMessage } from '../Api/http';
import { GetUserTypes, PostUserType, UpdateUserType, DeleteUserType } from '../Api/Api';
import { brandColor } from '../theme';
import { useCrumbActionsSetter } from '../utils/crumbActions';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_BORDER = '#B3D3E8';
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const tonalBtn = { bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}`, fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#D0E5F3' } };
const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#0F4F7B', boxShadow: 'none' } };
const label = { fontSize: 12.5, fontWeight: 600, color: '#334155', mb: 0.6 };
const field = { '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: 14, bgcolor: '#F8FAFC', '& fieldset': { borderColor: '#E5E7EB' }, '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 } } };

const COLORS = ['#135F93', '#0EA5E9', '#16A34A', '#F59E0B', '#E11D48', '#0E4A75', '#0891B2', '#DB2777', '#2D9CDB', '#F5A623'];
const initials = (s = '') => s.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const EMPTY_FORM = { name: '', description: '', accentColour: PRIMARY, attendanceMode: DEFAULT_ATTENDANCE_MODE };

export default function RolesPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const myUserTypeId = useSelector(selectUserTypeId);

    const [roles, setRoles] = useState([]);
    const [summary, setSummary] = useState(null);
    const [totalModules, setTotalModules] = useState(0);

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [confirmDel, setConfirmDel] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [snack, setSnack] = useState({ msg: '', severity: 'success' });

    const notify = (msg, severity = 'success') => setSnack({ msg, severity });

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const { data: body } = await http.get(GetUserTypes);
            if (body?.error) throw new Error(body.message || 'Could not load roles.');

            const d = body?.data || {};
            const list = d.roles || [];
            setRoles(list);
            setSummary(d.summary || null);
            setTotalModules(d.totalModules || 0);

            dispatch(syncRolesFromApi(list));
        } catch (err) {
            setLoadError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load roles.') : err.message);
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(''); setOpen(true); };
    const openEdit = (r) => {
        setEditing(r.id);
        setForm({
            name: r.name,
            description: r.description || '',
            accentColour: brandColor(r.accentColour),
            attendanceMode: attendanceModeOf(r.attendanceMode).key,
            // The API preserves the mode when the field is omitted, so only send
            // it once the admin actually picks one — otherwise a rename against a
            // build that doesn't return attendanceMode would reset it to hybrid.
            modeTouched: r.attendanceMode != null,
        });
        setFormError('');
        setOpen(true);
    };

    const save = async () => {
        const name = form.name.trim();
        if (!name) { setFormError('Enter a role name.'); return; }

        if (roles.some((r) => r.id !== editing && r.name.trim().toLowerCase() === name.toLowerCase())) {
            setFormError(`A role named "${name}" already exists.`);
            return;
        }

        setSaving(true);
        setFormError('');
        try {
            const payload = { name, description: form.description.trim(), accentColour: form.accentColour };
            if (!editing || form.modeTouched) payload.attendanceMode = form.attendanceMode;
            const { data: body } = editing
                ? await http.put(UpdateUserType, { id: editing, ...payload })
                : await http.post(PostUserType, payload);

            if (body?.error) throw new Error(body.message || 'Could not save the role.');

            setOpen(false);
            notify(editing ? `Role "${name}" updated.` : `Role "${name}" created.`);
            await load();
        } catch (err) {
            setFormError(apiErrorMessage(err, 'Could not save the role.'));
        } finally {
            setSaving(false);
        }
    };

    const canDelete = (r) => !r.isSystem && (r.usersCount ?? 0) === 0;

    const remove = async () => {
        const r = confirmDel;

        if (!canDelete(r)) {
            setConfirmDel(null);
            notify(`"${r.name}" still has ${r.usersCount} user(s). Move them to another role first.`, 'warning');
            return;
        }

        setDeleting(true);
        try {
            const { data: body } = await http.delete(`${DeleteUserType}?id=${r.id}`);

            if (body?.error || body?.data?.deleted !== true) {
                throw new Error(body?.message || 'The role was not deleted.');
            }

            setConfirmDel(null);
            notify(`Role "${r.name}" deleted.`);
            await load();
        } catch (err) {
            setConfirmDel(null);
            notify(apiErrorMessage(err, 'Could not delete the role.'), 'error');
        } finally {
            setDeleting(false);
        }
    };

    const KPIS = [
        { label: 'User Roles', value: summary?.userRoles ?? 0, icon: AdminPanelSettingsRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT },
        { label: 'Total Users', value: summary?.totalUsers ?? 0, icon: GroupsRoundedIcon, color: '#0EA5E9', bg: '#E0F2FE' },
        { label: 'System Roles', value: summary?.systemRoles ?? 0, icon: ShieldRoundedIcon, color: '#16A34A', bg: '#DCFCE7' },
        { label: 'Custom Roles', value: summary?.customRoles ?? 0, icon: TuneRoundedIcon, color: '#F59E0B', bg: '#FFF7ED' },
    ];

    // Reload and Create User Type sit at the right end of the breadcrumb row; fall back to the in-page header when no setter is provided.
    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { load, openCreate };
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip arrow title="Reload from server">
                    <span>
                        <IconButton onClick={() => actionsRef.current.load()} disabled={loading} sx={{ width: 42, height: 42, border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', color: '#5B6472', '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                            <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Button startIcon={<AddRoundedIcon />} onClick={() => actionsRef.current.openCreate()} sx={{ ...solidBtn, height: 42, px: 2.2 }}>Create User Type</Button>
            </Box>,
        );
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, loading]);
    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            {!setCrumbActions && (
                <Box sx={{ pb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexWrap: 'wrap', gap: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>Roles &amp; Access</Typography>
                        <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 0.2 }}>Define login roles and control what each user type can access</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Tooltip arrow title="Reload from server">
                            <span>
                                <IconButton onClick={load} disabled={loading} sx={{ width: 42, height: 42, border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', color: '#5B6472', '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                                    <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ ...tonalBtn, height: 42, px: 2.2 }}>
                            Create User Type
                        </Button>
                    </Stack>
                </Box>
            )}

            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                {KPIS.map((k) => (
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={k.label}>
                        <Box sx={{ ...card, p: 2.5, bgcolor: k.bg, border: `1px solid ${k.color}22` }}>
                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: k.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</Typography>
                                    {loading
                                        ? <Skeleton variant="text" width={44} height={40} sx={{ mt: 0.5 }} />
                                        : <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#0F172A', mt: 0.5 }}>{k.value}</Typography>}
                                </Box>
                                <Box sx={{ width: 36, height: 36, borderRadius: '7px', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <k.icon sx={{ color: k.color, fontSize: 22 }} />
                                </Box>
                            </Stack>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {loadError && !loading && (
                <Box sx={{ ...card, p: 4, textAlign: 'center' }}>
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 38, color: '#F87171' }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A', mt: 1 }}>Couldn’t load roles</Typography>
                    <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.5 }}>{loadError}</Typography>
                    <Button onClick={load} startIcon={<RefreshRoundedIcon />} sx={{ ...tonalBtn, mt: 2, height: 40, px: 2.4 }}>Try again</Button>
                </Box>
            )}

            <Grid container spacing={2.5}>
                {loading && [0, 1, 2].map((i) => (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={i}>
                        <Box sx={{ ...card, p: 2.5, height: 250 }}>
                            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                                <Skeleton variant="rounded" width={48} height={48} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="60%" height={24} />
                                    <Skeleton variant="text" width="30%" height={18} />
                                </Box>
                            </Stack>
                            <Skeleton variant="text" width="100%" height={20} sx={{ mt: 2 }} />
                            <Skeleton variant="text" width="80%" height={20} />
                            <Skeleton variant="rounded" width="100%" height={7} sx={{ mt: 3 }} />
                            <Skeleton variant="rounded" width="100%" height={38} sx={{ mt: 3 }} />
                        </Box>
                    </Grid>
                ))}

                {!loading && !loadError && roles.map((r) => {
                    const colour = brandColor(r.accentColour);
                    const access = r.accessCount ?? 0;
                    const users = r.usersCount ?? 0;
                    const pct = totalModules ? Math.round((access / totalModules) * 100) : 0;
                    const mode = attendanceModeOf(r.attendanceMode);
                    return (
                        <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={r.id}>
                            <Box sx={{ ...card, borderRadius: '12px', p: 2.4, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.8, transition: 'border-color .15s, box-shadow .15s', '&:hover': { borderColor: PRIMARY_BORDER, boxShadow: '0 6px 18px -10px rgba(16,24,40,0.25)' } }}>
                                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                                    <Box sx={{ width: 44, height: 44, borderRadius: '11px', bgcolor: `${colour}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: colour }}>{initials(r.name)}</Typography>
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A', lineHeight: 1.25 }} noWrap>{r.name}</Typography>
                                        <Stack direction="row" spacing={0.6} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.6 }}>
                                            <Chip
                                                size="small"
                                                icon={r.isSystem ? <LockRoundedIcon sx={{ fontSize: '12px !important' }} /> : undefined}
                                                label={r.isSystem ? 'System' : 'Custom'}
                                                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: r.isSystem ? '#E4F0F9' : '#FEF3C7', color: r.isSystem ? '#135F93' : '#B45309', '& .MuiChip-icon': { color: '#135F93', ml: 0.6 } }}
                                            />
                                            <Tooltip arrow title={mode.description}>
                                                <Chip
                                                    size="small"
                                                    icon={<mode.icon sx={{ fontSize: '12px !important' }} />}
                                                    label={mode.label}
                                                    sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: mode.bg, color: mode.color, '& .MuiChip-icon': { color: mode.color, ml: 0.6 } }}
                                                />
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                    {!r.isSystem && (
                                        <Stack direction="row" spacing={0.3} sx={{ flexShrink: 0 }}>
                                            <Tooltip arrow title="Edit role">
                                                <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                                                    <EditRoundedIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                arrow
                                                title={canDelete(r)
                                                    ? 'Delete role'
                                                    : `Can't delete — ${users} user${users === 1 ? '' : 's'} still assigned`}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setConfirmDel(r)}
                                                    sx={{
                                                        color: canDelete(r) ? '#94A3B8' : '#CBD2DD',
                                                        '&:hover': canDelete(r)
                                                            ? { color: '#DC2626', bgcolor: '#FEF2F2' }
                                                            : { color: '#B45309', bgcolor: '#FFF7ED' },
                                                    }}
                                                >
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                </Stack>

                                <Typography sx={{ fontSize: 12.5, color: '#64748B', lineHeight: 1.55, minHeight: 38 }}>
                                    {r.description || 'No description.'}
                                </Typography>

                                <Box>
                                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 0.7 }}>
                                        <Typography sx={{ fontSize: 12, color: '#64748B' }}>Module access</Typography>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0F172A' }}>
                                            {access}<Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: '#94A3B8' }}> / {totalModules}</Box>
                                        </Typography>
                                    </Stack>
                                    <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 5, bgcolor: '#EEF1F6', '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: colour } }} />
                                </Box>

                                <Stack direction="row" sx={{ mt: 'auto', pt: 1.6, borderTop: '1px solid #EEF0F6', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                    <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                        <PeopleAltRoundedIcon sx={{ fontSize: 17, color: users ? colour : '#CBD2DD' }} />
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: users ? '#0F172A' : '#94A3B8' }}>{users} user{users === 1 ? '' : 's'}</Typography>
                                        {users === 0 && <Typography sx={{ fontSize: 11.5, color: '#B4BBC6', fontStyle: 'italic' }}>· nobody assigned yet</Typography>}
                                    </Stack>
                                    <Stack direction="row" spacing={0.8}>
                                        <Button
                                            size="small" onClick={() => navigate(`/dashboard/roles/${r.id}/users`)}
                                            startIcon={canAssignRole(myUserTypeId, r.id) ? undefined : <LockRoundedIcon sx={{ fontSize: 14 }} />}
                                            sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 700, color: '#334155', border: '1px solid #E2E8F0', borderRadius: '7px', height: 34, px: 1.6, '&:hover': { bgcolor: '#F1F5F9' } }}
                                        >
                                            {canAssignRole(myUserTypeId, r.id) ? 'Users' : 'View Users'}
                                        </Button>
                                        {r.isSystem ? (
                                            <Tooltip arrow title="Predefined role — its access is locked and can't be edited">
                                                <span>
                                                    <Button size="small" disabled startIcon={<LockRoundedIcon sx={{ fontSize: 14 }} />}
                                                        sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 700, height: 34, px: 1.6, borderRadius: '7px', '&.Mui-disabled': { color: '#94A3B8', bgcolor: '#F8FAFC' } }}>
                                                        Locked
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Button size="small" onClick={() => navigate(`/dashboard/roles/${r.id}/access`)} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />}
                                                sx={{ ...tonalBtn, fontSize: 12.5, height: 34, px: 1.6 }}>
                                                Access
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            </Box>
                        </Grid>
                    );
                })}

                {!loading && !loadError && roles.length === 0 && (
                    <Grid size={12}>
                        <Box sx={{ ...card, p: 5, textAlign: 'center' }}>
                            <AdminPanelSettingsRoundedIcon sx={{ fontSize: 38, color: '#CBD2DD' }} />
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#64748B', mt: 1 }}>No login roles yet</Typography>
                            <Typography sx={{ fontSize: 13, color: '#98A0AE', mt: 0.4 }}>Create a user type to start controlling what people can reach.</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>

            <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: '7px', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <AdminPanelSettingsRoundedIcon sx={{ color: PRIMARY }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>{editing ? 'Edit User Type' : 'Create User Type'}</Typography>
                                <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>
                                    {editing ? 'Rename it, restyle it, or change how its people mark attendance' : 'Define a new login role for your organisation'}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={() => setOpen(false)} size="small" disabled={saving}><CloseRoundedIcon /></IconButton>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 2.5 }}>
                    {formError && (
                        <Alert severity="error" onClose={() => setFormError('')} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}>
                            {formError}
                        </Alert>
                    )}

                    <Typography sx={label}>Role name *</Typography>
                    <TextField
                        fullWidth size="small" placeholder="e.g. Finance Officer" autoFocus disabled={saving}
                        value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        sx={{ ...field, mb: 2 }}
                    />

                    <Typography sx={label}>Description</Typography>
                    <TextField
                        fullWidth size="small" multiline minRows={2} placeholder="e.g. Handles payroll finance" disabled={saving}
                        value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        sx={{ ...field, mb: 2 }}
                    />

                    <Typography sx={label}>Attendance mode</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                        {ATTENDANCE_MODES.map((m) => {
                            const on = form.attendanceMode === m.key;
                            return (
                                <Box
                                    key={m.key}
                                    onClick={() => !saving && setForm((f) => ({ ...f, attendanceMode: m.key, modeTouched: true }))}
                                    role="radio"
                                    aria-checked={on}
                                    sx={{
                                        flex: 1, minWidth: 0, p: 1.4, borderRadius: '9px', userSelect: 'none',
                                        cursor: saving ? 'default' : 'pointer',
                                        border: `1.5px solid ${on ? m.border : '#EEF1F6'}`,
                                        bgcolor: on ? m.bg : '#F8FAFC',
                                        transition: 'border-color .15s, background-color .15s',
                                        '&:hover': saving ? {} : { borderColor: on ? m.color : '#D8DEE8' },
                                    }}
                                >
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.6 }}>
                                        <Box sx={{ width: 28, height: 28, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', border: `1px solid ${on ? m.border : '#E5E7EB'}` }}>
                                            <m.icon sx={{ fontSize: 16, color: on ? m.color : '#94A3B8' }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: on ? '#0F172A' : '#475569', lineHeight: 1.2 }} noWrap>{m.label}</Typography>
                                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: on ? m.color : '#94A3B8' }} noWrap>{m.short}</Typography>
                                        </Box>
                                    </Stack>
                                    <Typography sx={{ fontSize: 11, color: '#64748B', lineHeight: 1.45 }}>{m.description}</Typography>
                                </Box>
                            );
                        })}
                    </Stack>
                    <Typography sx={{ fontSize: 11, color: '#98A0AE', mb: 2 }}>
                        Manually entered attendance is always allowed, whichever mode you pick.
                    </Typography>

                    <Typography sx={label}>Accent colour</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {COLORS.map((c) => (
                            <Box
                                key={c}
                                onClick={() => !saving && setForm((f) => ({ ...f, accentColour: c }))}
                                sx={{
                                    width: 30, height: 30, borderRadius: '50%', bgcolor: c, cursor: saving ? 'default' : 'pointer',
                                    border: form.accentColour === c ? '3px solid #fff' : '3px solid transparent',
                                    boxShadow: form.accentColour === c ? `0 0 0 2px ${c}` : '0 0 0 1px #E5E7EB',
                                }}
                            />
                        ))}
                    </Stack>

                    <Box sx={{ mt: 2.2, p: 1.8, borderRadius: '9px', border: '1px solid #EEF1F6', bgcolor: '#F8FAFC' }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>Preview</Typography>
                        <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: `linear-gradient(135deg, ${form.accentColour}, ${form.accentColour}BB)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#fff' }}>{initials(form.name) || '—'}</Typography>
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }} noWrap>{form.name.trim() || 'Role name'}</Typography>
                                <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>{form.description.trim() || 'No description'}</Typography>
                            </Box>
                            {(() => {
                                const m = attendanceModeOf(form.attendanceMode);
                                return (
                                    <Chip
                                        size="small"
                                        icon={<m.icon sx={{ fontSize: '13px !important' }} />}
                                        label={m.label}
                                        sx={{ flexShrink: 0, height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: m.bg, color: m.color, '& .MuiChip-icon': { color: m.color, ml: 0.6 } }}
                                    />
                                );
                            })()}
                        </Stack>
                    </Box>

                    {!editing && (
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '7px' }}>
                            <Typography sx={{ fontSize: 11.5, color: '#78350F' }}>
                                The new role starts with <strong>Dashboard</strong> access only. Grant the rest from its <strong>Access</strong> screen.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setOpen(false)} disabled={saving} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600, borderRadius: '7px' }}>Cancel</Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        startIcon={saving
                            ? <CircularProgress size={16} thickness={5} sx={{ color: PRIMARY }} />
                            : (editing ? <SaveRoundedIcon sx={{ fontSize: 18 }} /> : <AddRoundedIcon />)}
                        sx={{ ...tonalBtn, px: 2.5, height: 40 }}
                    >
                        {saving ? (editing ? 'Saving…' : 'Creating…') : (editing ? 'Save Changes' : 'Create Role')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(confirmDel)} onClose={() => !deleting && setConfirmDel(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                {confirmDel && !canDelete(confirmDel) ? (
                    <>
                        <DialogContent sx={{ pt: 3.5, pb: 2, textAlign: 'center' }}>
                            <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#FFF7ED', border: '4px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.8 }}>
                                <GroupsRoundedIcon sx={{ fontSize: 30, color: '#B45309' }} />
                            </Box>
                            <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#111827', mb: 0.8 }}>This role still has users</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#6B7280', px: 1, lineHeight: 1.6 }}>
                                <strong style={{ color: '#111827' }}>{confirmDel.name}</strong> is assigned to{' '}
                                <strong style={{ color: '#B45309' }}>{confirmDel.usersCount} user{confirmDel.usersCount === 1 ? '' : 's'}</strong>.
                                A role can only be deleted once nobody is using it — otherwise those users would be left without a login type.
                            </Typography>
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #EEF1F6', borderRadius: '7px' }}>
                                <Typography sx={{ fontSize: 11.5, color: '#64748B', textAlign: 'left' }}>
                                    Move {confirmDel.usersCount === 1 ? 'the user' : 'all users'} to another role first, then delete this one.
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
                            <Button fullWidth onClick={() => setConfirmDel(null)} sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '7px', height: 40, border: '1px solid #E5E7EB', color: '#374151' }}>
                                Close
                            </Button>
                            <Button
                                fullWidth
                                onClick={() => { const id = confirmDel.id; setConfirmDel(null); navigate(`/dashboard/roles/${id}/users`); }}
                                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
                                sx={{ ...tonalBtn, height: 40, fontSize: 13 }}
                            >
                                View users
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogContent sx={{ pt: 3.5, pb: 2, textAlign: 'center' }}>
                            <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#FEF2F2', border: '4px solid #FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.8 }}>
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 30, color: '#DC2626' }} />
                            </Box>
                            <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#111827', mb: 0.8 }}>Delete this role?</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#6B7280', px: 1.5, lineHeight: 1.5 }}>
                                <strong style={{ color: '#111827' }}>{confirmDel?.name}</strong> and its access configuration will be removed. No users are assigned to it. This cannot be undone.
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
                            <Button fullWidth onClick={() => setConfirmDel(null)} disabled={deleting} sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '7px', height: 40, border: '1px solid #E5E7EB', color: '#374151' }}>Cancel</Button>
                            <Button
                                fullWidth variant="contained" onClick={remove} disabled={deleting}
                                startIcon={deleting ? <CircularProgress size={15} thickness={5} sx={{ color: '#fff' }} /> : <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                                sx={{ textTransform: 'none', fontSize: 13, fontWeight: 700, borderRadius: '7px', height: 40, bgcolor: '#DC2626', color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: '#B91C1C' }, '&.Mui-disabled': { bgcolor: '#DC2626', color: '#fff', opacity: 0.7 } }}
                            >
                                {deleting ? 'Deleting…' : 'Delete'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Snackbar open={Boolean(snack.msg)} autoHideDuration={3200} onClose={() => setSnack({ msg: '', severity: 'success' })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack({ msg: '', severity: 'success' })} severity={snack.severity} variant="filled" sx={{ borderRadius: '7px' }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
